import apiClient from '@/modules/shared/services/apiClient';
import { ApiResponse, ProgramFormDto } from '@/modules/shared/types';
import type { GridFilterGroup } from '@/modules/shared/types/gridView';
import type { PatientLifecycleStatus, PatientGovernanceAction } from '../types/patientContract';

export interface PatientDto {
  patientId: string;
  patientCode?: string | null;
  code?: string | null;
  displayCode?: string | null;
  programId: string;
  hospitalId?: string | null;
  hospitalName?: string | null;
  hcpId?: string | null;
  hcpName?: string | null;
  formId: string;
  formVersion: number;
  formName?: string;
  formData?: Record<string, any>;
  status: PatientLifecycleStatus;
  currentWorkflowStepId?: string | null;
  currentWorkflowStepName?: string | null;
  workflowState?: string | null;
  createdAt: string;
  createdBy: string;
  createdByRole: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByRole?: string;
  archivedInfo?: PatientGovernanceAction | null;
  deletedInfo?: PatientGovernanceAction | null;
}

export interface PatientListDto {
  patientId: string;
  patientCode?: string | null;
  code?: string | null;
  displayCode?: string | null;
  formId: string;
  hospitalId?: string | null;
  hospitalName?: string | null;
  hcpId?: string | null;
  hcpName?: string | null;
  patientName?: string;
  status: PatientLifecycleStatus;
  currentWorkflowStepId?: string | null;
  currentWorkflowStepName?: string | null;
  workflowState?: string | null;
  createdAt: string;
  activeCasesCount: number;
  pendingCasesCount: number;
  closedCasesCount: number;
  caseStatusSummary: string;
  activeCaseNumber?: string | null;
  totalAssistanceAmount: number;
  caseSummary?: {
    totalCases: number;
    activeCases: number;
    pendingCases: number;
    completedCases: number;
    rejectedCases?: number;
    archivedCases?: number;
  };
  cases?: Array<{
    caseId: string;
    caseNumber: string;
    status: string;
    benefitPlanName?: string | null;
    medicationName?: string | null;
    currentCycleNumber?: number | null;
    benefitPlan?: string | null;
    medication?: string | null;
    cycleNumber?: number | null;
    enrollmentDate?: string | null;
    assignedUser?: string | null;
    lastActivity?: string | null;
    lastActivityDate?: string | null;
    lastActivityAt?: string | null;
  }>;
  registrationSource?: string | null;
  registrationApprovalStatus?: string | null;
  formData?: Record<string, any>;
  archivedInfo?: PatientGovernanceAction | null;
  deletedInfo?: PatientGovernanceAction | null;
}

export interface PatientPagedResultDto {
  patients: PatientListDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  insights?: {
    noActiveCaseCount: number;
    selfRegisteredCount: number;
    ccRegisteredCount: number;
    pendingApprovalCount: number;
    notLinkedToHcpCount: number;
    notAssignedToHospitalCount: number;
  };
}

export interface PatientListQueryParams {
  page?: number;
  pageSize?: number;
  visibilityScope?: 'active' | 'archived' | 'deleted' | 'all';
  statusScope?: 'active' | 'archived';
  status?: string;
  searchTerm?: string;
  workflowStepId?: string;
  fromDate?: string;
  toDate?: string;
  hospitalId?: string;
  hcpId?: string;
  patientId?: string;
  assignedToMe?: boolean;
  actionableOnly?: boolean;
  filters?: GridFilterGroup;
}

export interface CreatePatientDto {
  programId: string;
  hospitalId?: string;
  hcpId?: string;
  formId: string;
  formVersion: number;
  formData: Record<string, any>;
}

export interface UpdatePatientDto {
  formData: Record<string, any>;
}

export interface ResendHcpApprovalEmailRequestDto {
  reasonCode: string;
  comments?: string;
}

export interface ResendHcpApprovalEmailResponseDto {
  programId: string;
  patientId: string;
  hcpId?: string | null;
  hcpName?: string | null;
  hcpEmail?: string | null;
  reasonCode: string;
  comments?: string | null;
  expiredTokenCount: number;
  newTokenExpiresAtUtc: string;
  resentAtUtc: string;
}

export const patientService = {
  async getPatients(
    programId: string,
    query: PatientListQueryParams = {}
  ): Promise<ApiResponse<PatientPagedResultDto>> {
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 10),
    });

    if (query.status) {
      params.append('status', query.status);
    }
    const effectiveVisibilityScope = query.visibilityScope ?? query.statusScope;
    if (effectiveVisibilityScope) {
      params.append('visibilityScope', effectiveVisibilityScope);
    }
    if (query.searchTerm) {
      params.append('searchTerm', query.searchTerm);
    }
    if (query.workflowStepId) {
      params.append('workflowStepId', query.workflowStepId);
    }
    if (query.fromDate) {
      params.append('fromDate', query.fromDate);
    }
    if (query.toDate) {
      params.append('toDate', query.toDate);
    }
    if (query.hospitalId) {
      params.append('hospitalId', query.hospitalId);
    }
    if (query.hcpId) {
      params.append('hcpId', query.hcpId);
    }
    if (query.patientId) {
      params.append('patientId', query.patientId);
    }
    if (query.assignedToMe) {
      params.append('assignedToMe', 'true');
    }
    if (query.actionableOnly) {
      params.append('actionableOnly', 'true');
    }
    if (query.filters && query.filters.rules.length > 0) {
      params.append('filters', JSON.stringify(query.filters));
    }

    const response = await apiClient.get<ApiResponse<PatientPagedResultDto>>(
      `/programs/${programId}/patients?${params.toString()}`
    );
    return response.data;
  },

  async getPatientById(programId: string, patientId: string): Promise<ApiResponse<PatientDto>> {
    const response = await apiClient.get<ApiResponse<PatientDto>>(
      `/programs/${programId}/patients/${patientId}`
    );
    return response.data;
  },

  async getAvailableForms(programId: string): Promise<ApiResponse<ProgramFormDto[]>> {
    const response = await apiClient.get<ApiResponse<ProgramFormDto[]>>(
      `/programs/${programId}/patients/forms`
    );
    return response.data;
  },

  async createPatient(programId: string, dto: CreatePatientDto): Promise<ApiResponse<PatientDto>> {
    const response = await apiClient.post<ApiResponse<PatientDto>>(
      `/programs/${programId}/patients`,
      dto
    );
    return response.data;
  },

  async updatePatient(
    programId: string,
    patientId: string,
    dto: UpdatePatientDto
  ): Promise<ApiResponse<PatientDto>> {
    const response = await apiClient.put<ApiResponse<PatientDto>>(
      `/programs/${programId}/patients/${patientId}`,
      dto
    );
    return response.data;
  },

  async resendHcpApprovalEmail(
    programId: string,
    patientId: string,
    dto: ResendHcpApprovalEmailRequestDto
  ): Promise<ApiResponse<ResendHcpApprovalEmailResponseDto>> {
    const response = await apiClient.post<ApiResponse<ResendHcpApprovalEmailResponseDto>>(
      `/programs/${programId}/patients/${patientId}/resend-hcp-approval`,
      dto
    );
    return response.data;
  },

  /**
   * Get patients under a specific HCP
   */
  async getPatientsByHcpId(
    programId: string,
    hcpId: string,
    page: number = 1,
    pageSize: number = 10,
    status?: string,
    visibilityScope: 'active' | 'archived' | 'deleted' | 'all' = 'active'
  ): Promise<ApiResponse<PatientPagedResultDto>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (status) {
      params.append('status', status);
    }
    params.append('visibilityScope', visibilityScope);
    const response = await apiClient.get<ApiResponse<PatientPagedResultDto>>(
      `/programs/${programId}/patients/hcp/${hcpId}?${params.toString()}`
    );
    return response.data;
  },
};
